import PropTypes from 'prop-types';
import { useState } from 'react';
import { createRefetchContainer, graphql } from 'react-relay/legacy.js';
import { connect } from 'react-redux';
import { handleUserRefetch } from '../helpers/sbCreatorsRootUserRefetch.ts';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes.ts';

/* Components */
import { SbRespCreatorsComponents as SbRespCreatorsRootComponents } from '../SbRespCreatorsComponents/SbRespCreatorsComponents.jsx';
import { SbSharedHead } from '../SbSharedHead/SbSharedHead.jsx';
import {
    RecentlyViewedItemsProvider,
    TABLET,
    DESKTOP,
} from 'dibs-recent-history/exports/RecentlyViewedItemsProvider.ts';
import { SbSharedFavoritesNudgeToast } from '../../shared/SharedFavoritesNudge/SbSharedFavoritesNudgeToast/SbSharedFavoritesNudgeToast.tsx';

/* Hooks */
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { useSharedUserAndRelayVarsLoader } from '../../shared/SharedUserAndRelayVarLoader.tsx';
import { useDwelledOnPage } from 'dibs-buyer-layout/exports/dwelledOnPage.ts';
import { useSbSharedPageTracking } from '../hooks/useSbSharedPageTracking.ts';
import { sbRespRefineMenuFiltersMap } from '../SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap.ts';

const SbRespCreatorsLayoutComponent = props => {
    const [isClient, setIsClient] = useState(false);
    const { isTablet } = useServerVarsContext();
    const { viewer, reinitTracking, relay } = props;
    const user = viewer.user || null;
    const userId = user?.serviceId;
    const { itemSearch } = viewer;
    useSharedUserAndRelayVarsLoader({ onUserRefetch: handleUserRefetch(relay, setIsClient) });

    useDwelledOnPage(PAGE_TYPE.SEARCH);
    useSbSharedPageTracking({
        viewer,
        itemSearch,
        user,
        reinitTracking,
        refineMenuFiltersMap: sbRespRefineMenuFiltersMap,
    });

    const { isItemFavoritingFeb2026Variant, isItemFavoritingFeb2026Control } =
        useServerVarsContext();

    return (
        <RecentlyViewedItemsProvider deviceType={isTablet ? TABLET : DESKTOP}>
            <SbSharedHead itemSearch={itemSearch} />
            <SbRespCreatorsRootComponents
                viewer={viewer}
                itemSearch={itemSearch}
                user={user}
                isClient={isClient}
            />
            <SbSharedFavoritesNudgeToast
                userId={userId}
                isItemFavoritingFeb2026Variant={isItemFavoritingFeb2026Variant}
                isItemFavoritingFeb2026Control={isItemFavoritingFeb2026Control}
            />
        </RecentlyViewedItemsProvider>
    );
};

SbRespCreatorsLayoutComponent.propTypes = {
    viewer: PropTypes.object,
    relay: PropTypes.object.isRequired,
    localeDomain: PropTypes.string.isRequired,
    reinitTracking: PropTypes.func,
};

const mapStateToProps = state => {
    const { intlVariables, relayVariables } = state;

    return {
        localeDomain: intlVariables.localeDomain,
        relayVariables: relayVariables.variables,
    };
};

export const SbRespCreatorsLayout = createRefetchContainer(
    connect(mapStateToProps)(SbRespCreatorsLayoutComponent),
    {
        viewer: graphql`
            fragment SbRespCreatorsLayout_viewer on Viewer {
                ...SbRespCreatorsComponents_viewer
                ...useSbSharedPageTracking_viewer
                user(userId: $userId) @include(if: $fetchUser) {
                    serviceId
                    ...useSbSharedPageTracking_user
                    ...SbRespCreatorsComponents_user
                }
                itemSearch(
                    userId: $personalizationId
                    guestId: $guestId
                    uriRef: $uriRef
                    originalUrl: $originalUrl
                    first: $first
                    page: $page
                    localeOrigin: $localeOrigin
                    regions: $regions
                    disableForceFacet: $disableForceFacet
                    disableNonParameterizedUrlRedirects: $disableNonParameterizedUrlRedirects
                    userCountryCode: $userCountryCode
                    regionsList: $regionsList
                    priceBookName: $priceBookName
                ) {
                    ...SbSharedHead_itemSearch
                    ...SbRespCreatorsComponents_itemSearch
                    ...SbSharedRefetchContainer_itemSearch
                    ...useSbSharedPageTracking_itemSearch
                }
            }
        `,
    },
    graphql`
        query SbRespCreatorsLayoutRefetchQuery(
            $userId: String = ""
            $fetchUser: Boolean!
            $followSearchPages: [String]
            $followSearchTypes: [types] = [SEARCH, USER_PREFERENCE]
            $rerankUserId: String = ""
            $rerankGuestId: String = ""
            $isClient: Boolean!
        ) {
            viewer {
                ...useSbSharedPageTracking_viewer
                user(userId: $userId) @include(if: $fetchUser) {
                    serviceId
                    ...useSbSharedPageTracking_user
                    ...SbRespCreatorsComponents_user
                }
            }
        }
    `
);
